import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ModulePermissions } from '@utils/permissions';
import type { AppAccess } from '@lib/linkedApps';

/**
 * Team role from the team management system
 */
export type TeamRole = 'Admin' | 'Regular' | 'ReadOnly';

/**
 * Team membership status
 */
export type TeamStatus = 'unknown' | 'checking' | 'member' | 'not_in_team' | 'deactivated';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'staff' | 'volunteer' | 'readonly';
  avatar?: string;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;

  // Team membership
  teamStatus: TeamStatus;
  teamRole: TeamRole | null;
  teamMemberId: string | null;
  modulePermissions: ModulePermissions | null;
  appAccess: AppAccess | null;

  // Session timeout modal
  showSessionTimeout: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setTeamMembership: (status: TeamStatus, role?: TeamRole | null, memberId?: string | null, modulePermissions?: ModulePermissions | null, appAccess?: AppAccess | null) => void;
  setShowSessionTimeout: (show: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: false,
      error: null,
      teamStatus: 'unknown',
      teamRole: null,
      teamMemberId: null,
      modulePermissions: null,
      appAccess: null,
      showSessionTimeout: false,

      setUser: (user) => set({ user, error: null }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setTeamMembership: (status, role = null, memberId = null, modulePermissions = null, appAccess = null) =>
        set({ teamStatus: status, teamRole: role, teamMemberId: memberId, modulePermissions, appAccess }),
      setShowSessionTimeout: (show) => set({ showSessionTimeout: show }),
      logout: () =>
        set({
          user: null,
          error: null,
          teamStatus: 'unknown',
          teamRole: null,
          teamMemberId: null,
          modulePermissions: null,
          appAccess: null,
          showSessionTimeout: false,
        }),
    }),
    {
      name: 'sca-uk-auth',
      storage: createJSONStorage(() => sessionStorage),
      // Persist user and team membership data.
      // Do NOT persist 'checking' — if page reloads mid-check, start from 'unknown'
      // so the check runs again rather than hanging on the loading screen forever.
      partialize: (state) => ({
        user: state.user,
        // Normalise transient states to 'unknown' before persisting so a reload
        // always triggers a fresh check rather than showing a permanent spinner.
        teamStatus: state.teamStatus === 'checking' ? 'unknown' : state.teamStatus,
        teamRole: state.teamRole,
        teamMemberId: state.teamMemberId,
        modulePermissions: state.modulePermissions,
        appAccess: state.appAccess,
      }),
    }
  )
);
